export function calculateRiskRewardRatio(
  entryPrice: number,
  takeProfit: number,
  stopLoss: number,
  isLong: boolean
): string {
  if (!entryPrice || !takeProfit || !stopLoss) {
    throw new Error('Missing required values for R/R calculation');
  }

  let reward: number;
  let risk: number;

  if (isLong) {
    reward = Math.abs(takeProfit - entryPrice);
    risk = Math.abs(entryPrice - stopLoss);
  } else {
    reward = Math.abs(entryPrice - takeProfit);
    risk = Math.abs(stopLoss - entryPrice);
  }

  if (risk === 0) {
    throw new Error('Risk cannot be zero');
  }

  // Normaliser le ratio pour avoir un risque de 1
  const normalizedReward = (reward / risk).toFixed(2);
  
  return `1:${normalizedReward}`;
}

interface AssetConfig {
  pipValue: number;
  contractSize: number;
  category: 'Forex' | 'Métaux' | 'Indices' | 'Crypto' | 'Pétrole' | 'Futures';
}

const ASSET_CONFIGS: { [key: string]: AssetConfig } = {
  // Forex Majors
  'EUR/USD': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  'GBP/USD': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  'USD/JPY': { pipValue: 0.01, contractSize: 100000, category: 'Forex' },
  'USD/CHF': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  'USD/CAD': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  'AUD/USD': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  'NZD/USD': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  
  // Forex Crosses
  'EUR/GBP': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  'EUR/JPY': { pipValue: 0.01, contractSize: 100000, category: 'Forex' },
  'GBP/JPY': { pipValue: 0.01, contractSize: 100000, category: 'Forex' },
  'EUR/AUD': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  'EUR/CAD': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  'EUR/CHF': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  'GBP/CHF': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  'GBP/AUD': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  'AUD/JPY': { pipValue: 0.01, contractSize: 100000, category: 'Forex' },
  'CHF/JPY': { pipValue: 0.01, contractSize: 100000, category: 'Forex' },
  'CAD/JPY': { pipValue: 0.01, contractSize: 100000, category: 'Forex' },
  'NZD/JPY': { pipValue: 0.01, contractSize: 100000, category: 'Forex' },
  'EUR/NZD': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  'GBP/NZD': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  'AUD/NZD': { pipValue: 0.0001, contractSize: 100000, category: 'Forex' },
  
  // Métaux
  'XAU/USD': { pipValue: 0.01, contractSize: 100, category: 'Métaux' },
  'XAG/USD': { pipValue: 0.001, contractSize: 5000, category: 'Métaux' },
  'XPT/USD': { pipValue: 0.01, contractSize: 100, category: 'Métaux' },
  'XPD/USD': { pipValue: 0.01, contractSize: 100, category: 'Métaux' },
  'XAU/EUR': { pipValue: 0.01, contractSize: 100, category: 'Métaux' },
  'XAG/EUR': { pipValue: 0.001, contractSize: 5000, category: 'Métaux' },
  'XAU/GBP': { pipValue: 0.01, contractSize: 100, category: 'Métaux' },
  'XAU/AUD': { pipValue: 0.01, contractSize: 100, category: 'Métaux' },
  'XAU/CHF': { pipValue: 0.01, contractSize: 100, category: 'Métaux' },
  'XAU/JPY': { pipValue: 1, contractSize: 100, category: 'Métaux' },
  
  // Indices
  // DXY (US Dollar Index): CFD standard — 1 lot = 100 unités, tick 0.001 ($0.10/lot), 1.000 pt = $100/lot
  // pipValue=10 → pointValue=100 dans la formule Indices (aligné VantoTrade / MT5 CFD)
  'DXY': { pipValue: 10, contractSize: 1, category: 'Indices' },
  'USDX': { pipValue: 10, contractSize: 1, category: 'Indices' },
  'US30': { pipValue: 1, contractSize: 1, category: 'Indices' },
  'SPX500': { pipValue: 0.1, contractSize: 1, category: 'Indices' },
  'NAS100': { pipValue: 0.1, contractSize: 1, category: 'Indices' },
  'GER40': { pipValue: 0.1, contractSize: 1, category: 'Indices' },
  'UK100': { pipValue: 0.1, contractSize: 1, category: 'Indices' },
  'FRA40': { pipValue: 0.1, contractSize: 1, category: 'Indices' },
  'ESP35': { pipValue: 0.1, contractSize: 1, category: 'Indices' },
  'ITA40': { pipValue: 0.1, contractSize: 1, category: 'Indices' },
  'SUI20': { pipValue: 0.1, contractSize: 1, category: 'Indices' },
  'JPN225': { pipValue: 1, contractSize: 1, category: 'Indices' },
  'AUS200': { pipValue: 0.1, contractSize: 1, category: 'Indices' },
  'HK50': { pipValue: 0.1, contractSize: 1, category: 'Indices' },
  'CN50': { pipValue: 0.1, contractSize: 1, category: 'Indices' },
  
  // Futures - Indices
  'DOW': { pipValue: 1, contractSize: 5, category: 'Futures' },        // Dow Jones Futures
  'ES': { pipValue: 0.25, contractSize: 50, category: 'Futures' },      // E-mini S&P 500
  'NQ': { pipValue: 0.25, contractSize: 20, category: 'Futures' },      // E-mini NASDAQ 100
  'YM': { pipValue: 1, contractSize: 5, category: 'Futures' },          // Mini Dow Jones
  'RTY': { pipValue: 0.1, contractSize: 50, category: 'Futures' },      // E-mini Russell 2000
  'EMD': { pipValue: 0.1, contractSize: 100, category: 'Futures' },     // E-mini S&P MidCap 400
  'DAX': { pipValue: 0.5, contractSize: 25, category: 'Futures' },      // DAX Futures
  'FDAX': { pipValue: 0.5, contractSize: 25, category: 'Futures' },     // Mini-DAX Futures
  'FESX': { pipValue: 1, contractSize: 10, category: 'Futures' },       // Euro STOXX 50
  'FFTSE': { pipValue: 0.5, contractSize: 10, category: 'Futures' },    // FTSE 100 Futures
  'FCE': { pipValue: 0.5, contractSize: 10, category: 'Futures' },      // CAC 40 Futures
  'FMIB': { pipValue: 5, contractSize: 5, category: 'Futures' },        // FTSE MIB Futures
  'FIBEX': { pipValue: 1, contractSize: 10, category: 'Futures' },      // IBEX 35 Futures
  'FSMI': { pipValue: 1, contractSize: 10, category: 'Futures' },       // SMI Futures
  'NIY': { pipValue: 5, contractSize: 5, category: 'Futures' },         // Nikkei 225 Futures
  'HSI': { pipValue: 1, contractSize: 50, category: 'Futures' },        // Hang Seng Futures
  'SPI': { pipValue: 1, contractSize: 25, category: 'Futures' },        // ASX 200 Futures
  
  // Futures - Matières premières
  'CL': { pipValue: 0.01, contractSize: 1000, category: 'Futures' },    // Crude Oil Futures
  'GC': { pipValue: 0.1, contractSize: 100, category: 'Futures' },      // Gold Futures
  'SI': { pipValue: 0.001, contractSize: 5000, category: 'Futures' },   // Silver Futures
  'HG': { pipValue: 0.0005, contractSize: 25000, category: 'Futures' }, // Copper Futures
  'PL': { pipValue: 0.1, contractSize: 50, category: 'Futures' },       // Platinum Futures
  'PA': { pipValue: 0.05, contractSize: 100, category: 'Futures' },     // Palladium Futures
  'NG': { pipValue: 0.001, contractSize: 10000, category: 'Futures' },  // Natural Gas Futures
  'RB': { pipValue: 0.0001, contractSize: 42000, category: 'Futures' }, // RBOB Gasoline Futures
  'HO': { pipValue: 0.0001, contractSize: 42000, category: 'Futures' }, // Heating Oil Futures
  
  // Futures - Produits agricoles
  'ZC': { pipValue: 0.25, contractSize: 50, category: 'Futures' },      // Corn Futures
  'ZW': { pipValue: 0.25, contractSize: 50, category: 'Futures' },      // Wheat Futures
  'ZS': { pipValue: 0.25, contractSize: 50, category: 'Futures' },      // Soybean Futures
  'ZM': { pipValue: 0.1, contractSize: 100, category: 'Futures' },      // Soybean Meal Futures
  'ZL': { pipValue: 0.0001, contractSize: 60000, category: 'Futures' }, // Soybean Oil Futures
  'KC': { pipValue: 0.05, contractSize: 375, category: 'Futures' },     // Coffee Futures
  'SB': { pipValue: 0.01, contractSize: 1120, category: 'Futures' },    // Sugar Futures
  'CT': { pipValue: 0.01, contractSize: 500, category: 'Futures' },     // Cotton Futures
  'CC': { pipValue: 1, contractSize: 10, category: 'Futures' },         // Cocoa Futures
  'OJ': { pipValue: 0.05, contractSize: 150, category: 'Futures' },     // Orange Juice Futures
  
  // Futures - Obligations
  'ZB': { pipValue: 0.03125, contractSize: 1000, category: 'Futures' }, // U.S. Treasury Bond Futures
  'ZN': { pipValue: 0.015625, contractSize: 1000, category: 'Futures' },// 10-Year U.S. Treasury Note
  'ZF': { pipValue: 0.0078125, contractSize: 1000, category: 'Futures' },// 5-Year U.S. Treasury Note
  'ZT': { pipValue: 0.00390625, contractSize: 2000, category: 'Futures' },// 2-Year U.S. Treasury Note
  'GE': { pipValue: 0.0025, contractSize: 2500, category: 'Futures' },  // Eurodollar Futures
  'FGBL': { pipValue: 0.01, contractSize: 1000, category: 'Futures' },  // Euro-Bund Futures
  'FGBM': { pipValue: 0.01, contractSize: 1000, category: 'Futures' },  // Euro-Bobl Futures
  'FGBS': { pipValue: 0.005, contractSize: 1000, category: 'Futures' }, // Euro-Schatz Futures
  
  // Futures - Devises
  '6E': { pipValue: 0.0001, contractSize: 125000, category: 'Futures' }, // Euro FX Futures
  '6B': { pipValue: 0.0001, contractSize: 62500, category: 'Futures' },  // British Pound Futures
  '6J': { pipValue: 0.000001, contractSize: 12500000, category: 'Futures' }, // Japanese Yen Futures
  '6C': { pipValue: 0.0001, contractSize: 100000, category: 'Futures' }, // Canadian Dollar Futures
  '6A': { pipValue: 0.0001, contractSize: 100000, category: 'Futures' }, // Australian Dollar Futures
  '6N': { pipValue: 0.0001, contractSize: 100000, category: 'Futures' }, // New Zealand Dollar Futures
  '6S': { pipValue: 0.0001, contractSize: 125000, category: 'Futures' }, // Swiss Franc Futures
  '6M': { pipValue: 0.00001, contractSize: 500000, category: 'Futures' }, // Mexican Peso Futures
  
  // Futures - Crypto
  'BTC': { pipValue: 5, contractSize: 5, category: 'Futures' },         // Bitcoin Futures
  'ETH': { pipValue: 0.05, contractSize: 50, category: 'Futures' },     // Ethereum Futures
  'MBT': { pipValue: 0.5, contractSize: 0.1, category: 'Futures' },     // Micro Bitcoin Futures
  'MET': { pipValue: 0.05, contractSize: 0.1, category: 'Futures' },    // Micro Ethereum Futures
  'INDIA50': { pipValue: 0.1, contractSize: 1, category: 'Indices' },
  
  // Crypto
  'BTC/USD': { pipValue: 1, contractSize: 1, category: 'Crypto' },
  'ETH/USD': { pipValue: 1, contractSize: 1, category: 'Crypto' },
  'XRP/USD': { pipValue: 0.0001, contractSize: 1, category: 'Crypto' },
  'SOL/USD': { pipValue: 0.01, contractSize: 1, category: 'Crypto' },
  'ADA/USD': { pipValue: 0.0001, contractSize: 1, category: 'Crypto' },
  'BNB/USD': { pipValue: 0.1, contractSize: 1, category: 'Crypto' },
  'DOT/USD': { pipValue: 0.01, contractSize: 1, category: 'Crypto' },
  'DOGE/USD': { pipValue: 0.00001, contractSize: 1, category: 'Crypto' },
  'AVAX/USD': { pipValue: 0.01, contractSize: 1, category: 'Crypto' },
  'MATIC/USD': { pipValue: 0.0001, contractSize: 1, category: 'Crypto' },
  'LINK/USD': { pipValue: 0.01, contractSize: 1, category: 'Crypto' },
  'UNI/USD': { pipValue: 0.01, contractSize: 1, category: 'Crypto' },
  'SHIB/USD': { pipValue: 0.00000001, contractSize: 1, category: 'Crypto' },
  'LTC/USD': { pipValue: 0.01, contractSize: 1, category: 'Crypto' },
  'ATOM/USD': { pipValue: 0.01, contractSize: 1, category: 'Crypto' },
  'XLM/USD': { pipValue: 0.0001, contractSize: 1, category: 'Crypto' },
  'BCH/USD': { pipValue: 0.1, contractSize: 1, category: 'Crypto' },
  'BTC/EUR': { pipValue: 1, contractSize: 1, category: 'Crypto' },
  'ETH/EUR': { pipValue: 1, contractSize: 1, category: 'Crypto' },
  'BTC/GBP': { pipValue: 1, contractSize: 1, category: 'Crypto' },
  'ETH/GBP': { pipValue: 1, contractSize: 1, category: 'Crypto' },
  'BTC/JPY': { pipValue: 100, contractSize: 1, category: 'Crypto' },
  'ETH/BTC': { pipValue: 0.00001, contractSize: 1, category: 'Crypto' },
  
  // Pétrole
  'USOIL': { pipValue: 0.01, contractSize: 1000, category: 'Pétrole' }, // WTI Crude Oil
  'UKOIL': { pipValue: 0.01, contractSize: 1000, category: 'Pétrole' },  // Brent Crude Oil
  'NATGAS': { pipValue: 0.001, contractSize: 10000, category: 'Pétrole' } // Natural Gas
};

/**
 * Détermine la valeur du pip et la taille du contrat pour une paire Forex
 * en fonction des devises impliquées
 */
const generateForexConfig = (asset: string): AssetConfig => {
  // Vérifier que c'est une paire Forex valide
  if (!asset.includes('/') || asset.split('/').length !== 2) {
    throw new Error(`Format de paire Forex invalide: ${asset}`);
  }

  const [base, quote] = asset.split('/');
  
  // Déterminer la valeur du pip en fonction de la devise de cotation
  let pipValue = 0.0001; // Valeur par défaut pour la plupart des paires
  
  // Ajuster la valeur du pip pour les paires avec JPY
  if (quote === 'JPY') {
    pipValue = 0.01;
  }
  
  // Taille standard du contrat pour le Forex
  const contractSize = 100000;
  
  return {
    pipValue,
    contractSize,
    category: 'Forex'
  };
};

// Import pour les types crypto
interface CryptoFilters {
  price: {
    minPrice: string;
    maxPrice: string;
    tickSize: string;
  };
  lot: {
    minQty: string;
    maxQty: string;
    stepSize: string;
  };
  notional: {
    minNotional: string;
  };
}

interface CryptoSymbol {
  symbol: string;
  status: string;
  baseAsset: string;
  quoteAsset: string;
  filters: CryptoFilters;
}

// Fonction pour obtenir la configuration d'un actif
const getAssetConfig = (asset: string, cryptoData?: CryptoSymbol): AssetConfig => {
  const normalizedAsset = asset === 'USDX' ? 'DXY' : asset;

  // Vérifier si la configuration existe déjà
  const config = ASSET_CONFIGS[normalizedAsset];
  
  if (config) {
    return config;
  }
  
  // Si on a des données crypto de l'API, utiliser ces données
  if (cryptoData && cryptoData.symbol === asset) {
    return generateCryptoConfig(cryptoData);
  }
  
  // Si c'est probablement une crypto (contient USDT, BTC, ETH, etc.), générer une config par défaut
  if (asset.includes('USDT') || asset.includes('BTC') || asset.includes('ETH') || 
      asset.includes('BUSD') || asset.includes('USD') || asset.includes('EUR')) {
    return generateDefaultCryptoConfig(asset);
  }
  
  // Si la configuration n'existe pas, essayer de la générer automatiquement
  if (asset.includes('/')) {
    // Vérifier si c'est une paire Forex
    const [base, quote] = asset.split('/');
    
    // Liste des devises Forex courantes
    const forexCurrencies = [
      'USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD', 
      'SEK', 'NOK', 'DKK', 'PLN', 'HUF', 'CZK', 'TRY', 'ZAR', 
      'MXN', 'SGD', 'HKD', 'CNH', 'CNY', 'THB', 'ILS', 'RUB',
      'INR', 'IDR', 'MYR', 'PHP', 'TWD', 'KRW', 'BRL', 'ARS',
      'CLP', 'COP', 'PEN', 'AED', 'SAR', 'QAR', 'KWD', 'BHD',
      'OMR', 'EGP', 'MAD', 'NGN', 'KES', 'GHS'
    ];
    
    // Si les deux devises sont des devises Forex, générer une configuration Forex
    if (forexCurrencies.includes(base) && forexCurrencies.includes(quote)) {
      return generateForexConfig(asset);
    }
  }
  
  // Si on ne peut pas générer de configuration, lancer une erreur
  throw new Error(`Configuration non trouvée pour l'actif: ${asset}`);
};

/**
 * Génère une configuration pour une crypto basée sur les données de l'API
 */
const generateCryptoConfig = (cryptoData: CryptoSymbol): AssetConfig => {
  // Utiliser tickSize comme pipValue et stepSize pour la contractSize
  const tickSize = parseFloat(cryptoData.filters.price.tickSize);
  const stepSize = parseFloat(cryptoData.filters.lot.stepSize);
  
  // Déterminer la valeur du pip basée sur le tickSize
  let pipValue = tickSize;
  
  // Pour les paires avec des prix élevés (BTC, ETH), ajuster
  if (cryptoData.baseAsset === 'BTC') {
    pipValue = Math.max(1, tickSize);
  } else if (cryptoData.baseAsset === 'ETH') {
    pipValue = Math.max(0.01, tickSize);
  } else if (cryptoData.quoteAsset === 'USDT' || cryptoData.quoteAsset === 'USD') {
    // Pour les paires contre USD/USDT, utiliser le tickSize directement
    pipValue = tickSize;
  }
  
  return {
    pipValue,
    contractSize: 1, // Pour les cryptos, on utilise généralement 1 comme taille de contrat
    category: 'Crypto'
  };
};

/**
 * Génère une configuration par défaut pour une crypto sans données API
 */
const generateDefaultCryptoConfig = (asset: string): AssetConfig => {
  let pipValue = 0.01; // Valeur par défaut
  
  // Ajuster selon le type d'asset
  if (asset.includes('BTC')) {
    pipValue = 1;
  } else if (asset.includes('ETH')) {
    pipValue = 0.01;
  } else if (asset.includes('USDT') || asset.includes('USD')) {
    pipValue = 0.001;
  }
  
  // console.log supprimé
  
  return {
    pipValue,
    contractSize: 1,
    category: 'Crypto'
  };
};

export const calculateLotSize = (
  asset: string,
  entryPrice: number,
  stopLoss: number,
  riskPercentage: number,
  accountBalance: number,
  accountCurrency: string = 'USD',
  cryptoData?: CryptoSymbol
): number => {
  // Vérifier les entrées
  if (entryPrice == null || isNaN(entryPrice) || 
      stopLoss == null || isNaN(stopLoss) || 
      riskPercentage == null || isNaN(riskPercentage) || 
      accountBalance == null || isNaN(accountBalance)) {
    throw new Error('Toutes les valeurs sont requises pour le calcul');
  }

  // Obtenir la configuration de l'actif
  const config = getAssetConfig(asset, cryptoData);
  
  // Calculer le montant à risquer
  const riskAmount = accountBalance * (riskPercentage / 100);
  
  // Calculer les points de risque
  const pointsRisked = Math.abs(entryPrice - stopLoss);

  let lotSize: number;
  
  switch (config.category) {
    case 'Forex':
      // Pour le Forex, on utilise la formule standard
      lotSize = riskAmount / (pointsRisked * config.contractSize);
      break;
      
    case 'Métaux':
      // Pour les métaux, on ajuste selon la taille du contrat
      lotSize = riskAmount / (pointsRisked * config.contractSize);
      break;
      
    case 'Indices':
      // Pour les indices, on calcule en fonction de la valeur du point
      // La valeur du pip représente la valeur en monnaie d'un mouvement de 0.1 point
      // Donc pour 1 point complet, c'est pipValue * 10
      const pointValue = config.pipValue * 10;
      lotSize = riskAmount / (pointsRisked * pointValue);
      break;
      
    case 'Crypto':
      // Pour les cryptos, on calcule par rapport au prix direct
      lotSize = riskAmount / pointsRisked;
      break;
      
    case 'Pétrole':
      // Pour le pétrole, on utilise la même approche que pour les métaux
      lotSize = riskAmount / (pointsRisked * config.contractSize);
      break;
      
    case 'Futures':
      // Pour les futures, on utilise la valeur du point et la taille du contrat
      lotSize = riskAmount / (pointsRisked * config.pipValue * config.contractSize);
      break;
      
    default:
      throw new Error('Catégorie d\'actif non supportée');
  }

  // Arrondir à 2 décimales et s'assurer que c'est au moins 0.01
  return Math.max(0.01, Number(lotSize.toFixed(2)));
};

export const calculateProfitLoss = (
  asset: string,
  entryPrice: number,
  exitPrice: number,
  lotSize: number,
  type: 'LONG' | 'SHORT',
  cryptoData?: CryptoSymbol
): number => {
  const config = getAssetConfig(asset, cryptoData);
  const priceDiff = type === 'LONG' ? exitPrice - entryPrice : entryPrice - exitPrice;
  let profitLoss: number;

  switch (config.category) {
    case 'Forex':
      // Détecter si les prix sont dans un format non-standard SEULEMENT pour les paires non-JPY
      let normalizedPriceDiff = priceDiff;
      
      if (!asset.includes('JPY')) {
        // Détection pour les formats non-standard (seulement pour les prix vraiment anormaux)
        if (entryPrice > 10) {
          // Seulement pour les prix vraiment élevés (ex: 96.09 pour AUD/USD)
          normalizedPriceDiff = priceDiff / 100;
          // console.log supprimé
          // console.log supprimé
          // console.log supprimé
        } else {
          // Prix normaux, pas de normalisation
          // console.log supprimé
        }
      } else {
        // Pour les paires JPY, les prix sont normaux (ex: AUD/JPY = 92.74)
        // console.log supprimé
      }
      
      // Calcul corrigé du Forex
      const pipsCount = normalizedPriceDiff / config.pipValue;
      
      if (asset.includes('JPY')) {
        // Pour les paires JPY: 1 pip = environ 10 USD pour 1 lot standard
        // Formule simplifiée: pips * lot * 10 (approximation pour USD)
        profitLoss = pipsCount * lotSize * 10;
      } else {
        // Pour les paires non-JPY: 1 pip = 10 USD pour 1 lot standard
        // Formule simplifiée: pips * lot * 10
        profitLoss = pipsCount * lotSize * 10;
      }
      
      // console.log supprimé
      // console.log supprimé
      // console.log supprimé
      // console.log supprimé
      // console.log supprimé
      break;
      
    case 'Métaux':
      // Pour les métaux: (différence de prix) * (taille du lot) * (taille du contrat)
      profitLoss = priceDiff * lotSize * config.contractSize;
      break;
      
    case 'Indices':
      // Pour les indices: (différence de points) * (valeur du point) * (taille du lot)
      // La valeur du pip représente la valeur en monnaie d'un mouvement de 0.1 point
      // Donc pour 1 point complet, c'est pipValue * 10
      const pointValue = config.pipValue * 10;
      profitLoss = priceDiff * pointValue * lotSize;
      break;
      
    case 'Crypto':
      // Pour les cryptos: (différence de prix) * (taille du lot)
      profitLoss = priceDiff * lotSize;
      // Ajuster pour le BTC/USD et ETH/USD qui ont des prix élevés
      if (asset === 'BTC/USD' || asset === 'ETH/USD') {
        profitLoss = profitLoss * config.pipValue;
      }
      break;
      
    case 'Pétrole':
      // Pour le pétrole: (différence de prix) * (taille du lot) * (taille du contrat)
      profitLoss = priceDiff * lotSize * config.contractSize;
      break;
      
    case 'Futures':
      // Pour les futures: (différence de points) * (valeur du point) * (taille du lot) * (taille du contrat)
      profitLoss = priceDiff * config.pipValue * lotSize * config.contractSize;
      break;
      
    default:
      throw new Error('Catégorie d\'actif non supportée');
  }

  return Number(profitLoss.toFixed(2));
};

/**
 * Calcule la taille de lot à partir d'un P&L connu.
 * Principe : PL = plPerLot * lotSize → lotSize = PL / plPerLot
 * Retourne null si le calcul est impossible (prix identiques, asset inconnu, etc.)
 */
export const calculateLotSizeFromPL = (
  asset: string,
  entryPrice: number,
  exitPrice: number,
  profitLoss: number,
  type: 'LONG' | 'SHORT',
  cryptoData?: CryptoSymbol
): number | null => {
  try {
    if (!entryPrice || !exitPrice || entryPrice === exitPrice) return null;
    // P&L pour exactement 1 lot
    const plPerLot = calculateProfitLoss(asset, entryPrice, exitPrice, 1, type, cryptoData);
    if (!plPerLot || plPerLot === 0) return null;
    const lotSize = profitLoss / plPerLot;
    if (!isFinite(lotSize) || lotSize <= 0) return null;
    return Math.max(0.01, Number(lotSize.toFixed(2)));
  } catch {
    return null;
  }
};
